import React, { useMemo, useState } from "react";
import { Segmented } from "antd";
import { CompsTable } from "@ms/shared-components";
import { createHourlyPriceColumns, type HourlyPriceRange } from "./config";

export interface HourlyPriceByRoomRow {
  label?: string;
  priceData24h?: number[];
  priceData7d?: number[];
  currentPrice?: number;
  isStoreTotal?: boolean;
}

export interface HourlyPriceByRoomTypeProps {
  /** 各房型过去 24h / 7d 挂牌价（来自 proxy/hourly-price-by-room） */
  dataSource: HourlyPriceByRoomRow[];
  style?: React.CSSProperties;
  onTitleClick?: () => void;
  titleLeftAdornment?: React.ReactNode;
  disableTableAutoScroll?: boolean;
}

const TABLE_BODY_SCROLL_Y = 220;
const NODES_24H = 24;
const NODES_7D = 7;

function padSeries(arr: number[] | undefined, len: number): number[] {
  if (!Array.isArray(arr) || arr.length === 0) return new Array(len).fill(0);
  if (arr.length >= len) return arr.slice(0, len);
  return [...arr, ...new Array(len - arr.length).fill(0)];
}

const HourlyPriceByRoomType: React.FC<HourlyPriceByRoomTypeProps> = ({
  dataSource,
  style,
  onTitleClick,
  titleLeftAdornment,
  disableTableAutoScroll = false,
}) => {
  const [range, setRange] = useState<HourlyPriceRange>("24h");
  const columns = useMemo(() => createHourlyPriceColumns(range), [range]);

  const rows = useMemo(() => {
    if (!Array.isArray(dataSource)) return [];
    const len = range === "24h" ? NODES_24H : NODES_7D;
    return dataSource
      .filter((r) => !r.isStoreTotal)
      .map((r, i) => {
        const priceData =
          range === "24h"
            ? padSeries(r.priceData24h, NODES_24H)
            : padSeries(r.priceData7d, NODES_7D);
        return {
          ...r,
          id: r.label ? `hourly-${r.label}` : `hourly-row-${i}`,
          label: r.label ?? "",
          priceData,
          currentPrice: r.currentPrice,
        };
      });
  }, [dataSource, range]);

  return (
    <>
      <style>
        {`
          .hourly-price-by-room-table .ant-table-wrapper { height: 100% !important; display: flex !important; flex-direction: column !important; }
          .hourly-price-by-room-table .ant-table { height: 100% !important; display: flex !important; flex-direction: column !important; table-layout: fixed !important; }
          .hourly-price-by-room-table .ant-table-container { height: 100% !important; display: flex !important; flex-direction: column !important; }
          .hourly-price-by-room-table .ant-table-body { flex: 1 !important; min-height: 0 !important; overflow-y: auto !important; overflow-x: hidden !important; }
          .hourly-price-by-room-table .ant-table-tbody > tr { height: 32px !important; }
          .hourly-price-by-room-table .ant-table-tbody > tr > td { height: 32px !important; padding-top: 0 !important; padding-bottom: 0 !important; vertical-align: middle !important; overflow: hidden !important; }
          .hourly-price-by-room-table .ant-table-tbody > tr > td:last-child { max-width: 80px !important; overflow: hidden !important; }
          .hourly-price-by-room-table .ant-table-thead > tr { height: 40px !important; }
          .hourly-price-by-room-table .ant-table-thead > tr > th { height: 40px !important; padding-top: 0 !important; padding-bottom: 0 !important; vertical-align: middle !important; }
          .hourly-price-by-room-table .ant-table-measure-row { display: none !important; }
          .hourly-price-by-room-table .ant-table-tbody .ant-table-cell { overflow: hidden !important; }
        `}
      </style>
      <div
        data-component-id="table:hourly-price-by-room"
        style={{
          display: "flex",
          flexDirection: "column",
          width: 400,
          height: 300,
          border: "1px solid #f0f0f0",
          borderRadius: 4,
          boxSizing: "border-box",
          overflow: "hidden",
          flexShrink: 0,
          ...style,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 12px",
            borderBottom: "1px solid #f0f0f0",
            background: "#fff",
            flexShrink: 0,
            gap: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", flexShrink: 0, minWidth: 0 }}>
            {titleLeftAdornment && (
              <div style={{ marginLeft: -4, marginRight: 4, flexShrink: 0, display: "flex", alignItems: "center" }}>
                {titleLeftAdornment}
              </div>
            )}
            {onTitleClick ? (
              <button
                type="button"
                onClick={onTitleClick}
                aria-label="点击查看近期房型房价分析"
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: "#333",
                  cursor: "pointer",
                  userSelect: "none",
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  textAlign: "left",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "#1890ff"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "#333"; }}
                title="点击查看近期房型房价分析"
              >
                近期房型房价
              </button>
            ) : (
              <div style={{ fontSize: 14, fontWeight: 500, color: "#333" }}>
                近期房型房价
              </div>
            )}
          </div>
          <Segmented
            size="small"
            value={range}
            onChange={(v) => setRange(v as HourlyPriceRange)}
            options={[
              { label: "24小时", value: "24h" },
              { label: "7天", value: "7d" },
            ]}
          />
        </div>
        <div
          className="hourly-price-by-room-table"
          style={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            padding: "16px 8px",
            boxSizing: "border-box",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CompsTable
            tableProps={{
              dataSource: rows,
              columns,
              rowKey: "id",
              size: "small",
              pagination: false,
              showHeader: false,
              bordered: false,
              ...(disableTableAutoScroll
                ? { scroll: { y: TABLE_BODY_SCROLL_Y } }
                : {}),
            }}
            showColumnSetting={false}
            autoScroll={!disableTableAutoScroll}
          />
        </div>
      </div>
    </>
  );
};

export default HourlyPriceByRoomType;
